import { useDataProvider } from 'react-admin';
import { useTranslation } from 'react-i18next';
import { useCosmos } from 'providers/contexts/CosmosContext';
import {
  FetchFnParams,
  FetchedFormattedInputChoices,
} from 'components/Inputs/Dropdown/SelectDropdown/SelectDropdownInput';
import { InputChoice } from 'components/Inputs/Dropdown/dropdownConsts';
import { endash, hairSpace } from 'utility/constants';
import {
  BloxCustomDataProviderType,
  UserAccountSummary,
  ServiceSearchItem,
  ServiceTypeItem,
  PublicationListItem,
  GetUserAccountByIdResult,
} from 'utility/types/dataProvider';
import {
  getAuthorPrimaryText,
  getAuthorSecondaryText,
  getAuthorAccountTypeChip,
} from 'components/FormControls/ListSearchSelect/helpers/authorDisplayHelpers';
import {
  BroadcastChannelItem,
  BroadcastMessageItem,
  GetBroadcastChannelParams,
  GetBroadcastMessageParams,
} from 'providers/contexts/extensions/Broadcast';
import {
  SectionListItem,
  KeywordListItem,
  GetSiteTagListParams,
  SiteTagListItem,
  GeoTagListItem,
} from 'providers/contexts/extensions/SiteTaxonomy';

/**
 * ----------------------------------------
 * Dropdown Fetch Setup Hooks
 * ----------------------------------------
 */

export const useGetFetchAndFormatSections = () => {
  const dataProvider = useDataProvider<BloxCustomDataProviderType>();

  const fetchAndFormatSections = async ({
    filterTextDebounced,
    pageParam,
  }: FetchFnParams): Promise<FetchedFormattedInputChoices> => {
    try {
      const params = {
        query: filterTextDebounced,
        page: pageParam,
        limit: 99,
      };

      const result = await dataProvider.getSectionList(params);

      if (!result.data?.items) {
        throw new Error('[fetchAndFormatSections] result missing data.items!');
      }

      // Hairspace is for labels so the styles can 'break-word'
      // since the api returns one long string with no spaces
      const returnData: FetchedFormattedInputChoices = {
        total: result.data.total,
        next: result.data.next,
        prev: result.data.prev,
        items: result.data.items.map((section: SectionListItem) => {
          return {
            label: section?.path?.replace(/\//g, `${hairSpace}/${hairSpace}`),
            value: section?.path,
          };
        }),
      };

      return returnData;
    } catch (error) {
      throw error; // Input's useQuery should catch the error and handle it.
    }
  };

  return { fetchAndFormatSections };
};

export const useGetFetchAndFormatKeywords = () => {
  const dataProvider = useDataProvider<BloxCustomDataProviderType>();

  const fetchAndFormatKeywords = async ({
    filterTextDebounced,
    pageParam,
  }: FetchFnParams): Promise<FetchedFormattedInputChoices> => {
    try {
      const params = {
        query: filterTextDebounced,
        page: pageParam,
        limit: 99,
      };

      const result = await dataProvider.getKeywordList(params);

      if (!result.data?.items) {
        throw new Error('[fetchAndFormatKeywords] result missing data.items!');
      }
      // Hairspace is for labels so the styles can 'break-word'
      // since the api returns one long string with no spaces
      const returnData: FetchedFormattedInputChoices = {
        total: result.data.total,
        next: result.data.next,
        prev: result.data.prev,
        items: result.data.items.map((keyword: KeywordListItem) => {
          return {
            label: keyword?.path?.replace(/\//g, `${hairSpace}/${hairSpace}`),
            value: keyword?.path,
          };
        }),
      };

      return returnData;
    } catch (error) {
      throw error; // Input's useQuery should catch the error and handle it.
    }
  };

  return { fetchAndFormatKeywords };
};

export const useGetFetchAndFormatSiteTags = () => {
  const dataProvider = useDataProvider<BloxCustomDataProviderType>();

  const fetchAndFormatSiteTags = async ({
    filterTextDebounced,
    pageParam,
  }: FetchFnParams): Promise<FetchedFormattedInputChoices> => {
    try {
      const params = {
        query: filterTextDebounced,
        page: pageParam,
        limit: 99,
      } as GetSiteTagListParams;

      const result = await dataProvider.getSiteTagList(params);

      if (!result?.data?.items) {
        throw new Error('[fetchAndFormatSiteTags] result missing data.items!');
      }
      // Hairspace is for labels so the styles can 'break-word'
      // since the api returns one long string with no spaces
      const returnData: FetchedFormattedInputChoices = {
        total: result.data.total,
        next: result.data.next,
        prev: result.data.prev,
        items: result.data.items.map((siteTag: SiteTagListItem) => {
          return {
            label: siteTag?.path?.replace(/\//g, `${hairSpace}/${hairSpace}`),
            value: siteTag?.path,
          };
        }),
      };

      return returnData;
    } catch (error) {
      throw error;
    }
  };

  return { fetchAndFormatSiteTags };
};

export const useGetFetchAndFormatGeoTags = () => {
  const dataProvider = useDataProvider<BloxCustomDataProviderType>();

  const fetchAndFormatGeoTags = async ({
    filterTextDebounced,
    pageParam,
  }: FetchFnParams): Promise<FetchedFormattedInputChoices> => {
    try {
      const params = {
        query: filterTextDebounced,
        page: pageParam,
        limit: 99,
      };

      const result = await dataProvider.getGeoTagList(params);

      if (!result.data?.items) {
        throw new Error('[fetchAndFormatGeoTags] result missing data.items!');
      }
      // Hairspace is for labels so the styles can 'break-word'
      // since the api returns one long string with no spaces
      const returnData: FetchedFormattedInputChoices = {
        total: result.data.total,
        next: result.data.next,
        prev: result.data.prev,
        items: result.data.items.map((geoTag: GeoTagListItem) => {
          return {
            label: geoTag?.path?.replace(/\//g, `${hairSpace}/${hairSpace}`),
            value: geoTag?.path,
          };
        }),
      };

      return returnData;
    } catch (error) {
      throw error; // Input's useQuery should catch the error and handle it.
    }
  };

  return { fetchAndFormatGeoTags };
};

export const useGetFetchAndFormatAuthors = () => {
  const dataProvider = useDataProvider();
  const { userInfo } = useCosmos();

  const fetchAndFormatAuthors = async ({
    filterTextDebounced,
    pageParam,
  }: FetchFnParams): Promise<FetchedFormattedInputChoices> => {
    try {
      const params = {
        query: filterTextDebounced,
        page: pageParam,
        limit: 99,
      };

      const result = await dataProvider.getUserAccountSearch(params);

      if (!result.data?.items) {
        throw new Error('[fetchAndFormatAuthors] result missing data.items');
      }

      let items = result.data.items.map((author: UserAccountSummary) => {
        return {
          label: getAuthorPrimaryText(author),
          sublabel: getAuthorSecondaryText(author),
          value: author?.uuid,
          chip: getAuthorAccountTypeChip(author),
          description: author?.email,
        };
      });

      let total = result.data.total;

      // Surface the current staff/super user at the top of the *initial* list for
      // quick self-selection. Only do this when there's no active search (the API
      // already returns matching users when filtering) and only when they aren't
      // already in the page. Prepending unconditionally previously duplicated the
      // user (deduped out of the list) while still adding +1 to the total, which
      // produced an off-by-one like "61 of 62". See CMSADMIN-2530.
      const isStaff = userInfo?.account_type === 'staff';
      const isInitialList = !filterTextDebounced?.trim();
      const currentUserUuid = userInfo?.uuid;
      const alreadyInPage =
        !!currentUserUuid &&
        items.some((item: InputChoice) => item.value === currentUserUuid);

      if (isStaff && isInitialList && currentUserUuid && !alreadyInPage) {
        const currentUserItem = {
          label: getAuthorPrimaryText(userInfo),
          sublabel: getAuthorSecondaryText(userInfo),
          value: currentUserUuid,
          chip: getAuthorAccountTypeChip(userInfo),
          description: userInfo?.email,
        };
        // Prepend to highlight staff user and count them in the total.
        items = [currentUserItem, ...items];
        total += 1;
      }

      const returnData: FetchedFormattedInputChoices = {
        total,
        next: result.data.next,
        prev: result.data.prev,
        items,
      };

      return returnData;
    } catch (error) {
      throw error; // Input's useQuery should catch the error and handle it.
    }
  };

  return { fetchAndFormatAuthors };
};

/**
 * Module-level cache for fetched author data.
 * Shared across all component instances using this hook.
 * Prevents duplicate API calls when multiple components need the same authors.
 */
const fetchedAuthorCache = new Map<string, InputChoice>();

// Fetch unlabeled author items by UUID
// Used when an author is selected but wasnt in the initial paginated fetch
export const useGetFetchAndFormatUnlabeledAuthors = () => {
  const dataProvider = useDataProvider<BloxCustomDataProviderType>();

  const fetchAndFormatUnlabeledAuthors = async (
    uuids: string[],
  ): Promise<FetchedFormattedInputChoices> => {
    try {
      if (!uuids || uuids.length === 0) {
        return { total: 0, next: undefined, prev: undefined, items: [] };
      }

      // Separate UUIDs into cached and uncached
      const uncachedUuids: string[] = [];
      const cachedItems: InputChoice[] = [];

      for (const uuid of uuids) {
        const cached = fetchedAuthorCache.get(uuid);
        if (cached) {
          cachedItems.push(cached);
        } else {
          uncachedUuids.push(uuid);
        }
      }

      // If all requested authors are cached, return immediately
      if (uncachedUuids.length === 0) {
        return {
          total: cachedItems.length,
          next: undefined,
          prev: undefined,
          items: cachedItems,
        };
      }

      // Fetch uncached authors from the API
      const results = await Promise.all(
        uncachedUuids.map((uuid) =>
          dataProvider.getUserAccountSummaryById(uuid).catch((error: unknown) => {
            console.error(
              `[fetchAndFormatUnlabeledAuthors] Failed to fetch author ${uuid}:`,
              error,
            );
            return null;
          }),
        ),
      );

      // Filter out failed requests, format and cache successful responses
      results
        .filter(
          (
            result,
          ): result is GetUserAccountByIdResult & {
            data: NonNullable<GetUserAccountByIdResult['data']>;
          } => Boolean(result?.data),
        )
        .forEach((result) => {
          const author = result.data;
          const formattedItem: InputChoice = {
            label: getAuthorPrimaryText(author),
            sublabel: getAuthorSecondaryText(author) ?? undefined,
            value: author.uuid || '',
            chip: getAuthorAccountTypeChip(author),
            description: author.email ?? undefined,
          };
          if (formattedItem.value) {
            fetchedAuthorCache.set(formattedItem.value, formattedItem);
          }
        });

      // Combine cached and newly fetched, maintaining original UUID order
      const allItems = uuids
        .map((uuid) => fetchedAuthorCache.get(uuid))
        .filter((item): item is InputChoice => Boolean(item));

      return {
        total: allItems.length,
        next: undefined,
        prev: undefined,
        items: allItems,
      };
    } catch (error) {
      console.error('[fetchAndFormatUnlabeledAuthors] error:', error);
      throw error;
    }
  };

  return { fetchAndFormatUnlabeledAuthors };
};

/**
 * Cache an author choice to prevent redundant fetches.
 * Call this when a user selects an author from search results.
 * The full choice data is already available from the search result,
 * so we cache it to avoid fetching the summary later.
 */
export const cacheAuthorChoice = (choice: InputChoice): void => {
  if (choice.value) {
    fetchedAuthorCache.set(String(choice.value), choice);
  }
};

/**
 * Get cached author choices by their UUIDs.
 * Returns only the items that are in the cache.
 * Used to skip loading state when selected items were just cached from search.
 */
export const getCachedAuthorChoices = (uuids: string[]): InputChoice[] => {
  const cached: InputChoice[] = [];
  for (const uuid of uuids) {
    const item = fetchedAuthorCache.get(uuid);
    if (item) {
      cached.push(item);
    }
  }
  return cached;
};

export const useGetFetchAndFormatWorkflows = () => {
  const dataProvider = useDataProvider<BloxCustomDataProviderType>();

  const fetchAndFormatWorkflows = async (): Promise<FetchedFormattedInputChoices> => {
    try {
      const result = await dataProvider.getWorkflows();

      if (!result?.data || !Array.isArray(result?.data)) {
        throw new Error(
          '[useGetFetchAndFormatWorkflows] No result.data or is not an array!',
        );
      }

      // Flatten workflows + processes into a single array
      const flattened = result.data.flatMap((workflow) => {
        const workflowItem = {
          value: workflow?.id || '',
          label: workflow?.name || '',
        };
        const processItems = (workflow?.processes || []).map((proc) => ({
          value: proc?.id || '',
          label: `${workflow?.name || ''}${hairSpace}${endash}${hairSpace}${
            proc?.name || ''
          }`,
        }));
        return [workflowItem, ...processItems];
      });

      const final = {
        total: flattened.length,
        next: null,
        prev: null,
        items: flattened,
      };

      return final;
    } catch (error) {
      throw error;
    }
  };

  return { fetchAndFormatWorkflows };
};

// Batch edit workflows
export const useGetFetchAndFormatBatchEditWorkflows = () => {
  const dataProvider = useDataProvider<BloxCustomDataProviderType>();
  const { t } = useTranslation();

  const fetchAndFormatBatchEditWorkflows =
    async (): Promise<FetchedFormattedInputChoices> => {
      const result = await dataProvider.getWorkflows();
      if (!Array.isArray(result?.data)) {
        throw new Error('fetchAndFormatBatchEditWorkflows: Invalid data format');
      }

      const items = result.data.flatMap((workflow) => {
        const header = {
          value: `__header__${workflow.id}`,
          label: workflow.name || '',
          __isHeader: true,
        };

        const processes = (workflow?.processes || []).map((proc) => ({
          value: `${workflow.id}_${proc.id}`,
          label: `${proc.name}`,
          selectedLabel: `${workflow.name}: ${proc.name}`, // used in chips
        }));

        return [header, ...processes];
      });

      // Insert remove option at the top
      const withRemoveOption = [
        {
          value: 'remove',
          label: t('blox.editorial.asset.properties.workflow.actions.remove'),
        },
        ...items,
      ];

      return {
        total: withRemoveOption.length,
        next: null,
        prev: null,
        items: withRemoveOption,
      };
    };

  return { fetchAndFormatBatchEditWorkflows };
};

export const useGetFetchAndFormatServices = () => {
  const dataProvider = useDataProvider<BloxCustomDataProviderType>();

  const fetchAndFormatServices = async ({
    filterTextDebounced,
    pageParam,
  }: FetchFnParams): Promise<FetchedFormattedInputChoices> => {
    try {
      const params = {
        name: filterTextDebounced,
        is_active: true, // show only active services
        page: pageParam,
        limit: 99,
      };

      const result = await dataProvider.getServices(params);

      if (!result.data?.items) {
        throw new Error('[fetchAndFormatServices] result missing data.items');
      }

      return {
        total: result.data.total,
        next: result.data.next,
        prev: result.data.prev,
        items: result.data.items.map((service: ServiceSearchItem) => ({
          label: service.name || '',
          value: String(service.id),
        })),
      };
    } catch (error) {
      console.error('[fetchAndFormatServices] error:', error);
      throw error;
    }
  };

  return { fetchAndFormatServices };
};

export const useGetFetchAndFormatSvcTypes = () => {
  const dataProvider = useDataProvider<BloxCustomDataProviderType>();

  const fetchAndFormatSvcTypes = async ({
    filterTextDebounced,
    pageParam,
  }: FetchFnParams): Promise<FetchedFormattedInputChoices> => {
    try {
      const params = {
        title: filterTextDebounced,
        page: pageParam,
        limit: 99,
        activated: true,
      };

      const result = await dataProvider.getServiceTypes(params);

      if (!result.data?.items) {
        throw new Error('[fetchAndFormatSvcTypes] result missing data.items');
      }

      return {
        total: result.data.total,
        next: result.data.next,
        prev: result.data.prev,
        items: result.data.items.map((type: ServiceTypeItem) => ({
          label: type.title || '',
          value: String(type.id),
        })),
      };
    } catch (error) {
      console.error('[fetchAndFormatSvcTypes] error:', error);
      throw error;
    }
  };

  return { fetchAndFormatSvcTypes };
};

export const useGetFetchAndFormatPublications = () => {
  const dataProvider = useDataProvider<BloxCustomDataProviderType>();

  const fetchAndFormatPublications = async ({
    filterTextDebounced,
    pageParam,
  }: FetchFnParams): Promise<FetchedFormattedInputChoices> => {
    try {
      const params = {
        type: filterTextDebounced,
        is_active: true, // show only active services
        page: pageParam,
        limit: 99,
      };

      const result = await dataProvider.getPublicationList('publication', params);

      if (!result.data?.items) {
        throw new Error('[fetchAndFormatPublications] result missing data.items');
      }

      return {
        total: result.data.total,
        next: result.data.next,
        prev: result.data.prev,
        items: result.data.items.map((publication: PublicationListItem) => ({
          label: `${publication.description}  (${publication.code})` || '',
          value: publication.code,
        })),
      };
    } catch (error) {
      console.error('[fetchAndFormatPublications] error:', error);
      throw error;
    }
  };

  return { fetchAndFormatPublications };
};

/**
 * GET /broadcast/channel/ channels
 */

export const useGetFetchAndFormatBroadcastChannels = () => {
  const dataProvider = useDataProvider<BloxCustomDataProviderType>();

  const fetchAndFormatBroadcastChannels = async ({
    filterTextDebounced,
    pageParam,
  }: FetchFnParams): Promise<FetchedFormattedInputChoices> => {
    try {
      // Alphabetical sort by default for consistency
      const payload: GetBroadcastChannelParams = {
        query: {
          dir: 'ASC',
          sort: 'name',
          page: pageParam,
          limit: 99,
        },
      };

      const result = await dataProvider.getBroadcastChannel(payload);

      if (!result.data?.items) {
        throw new Error('[fetchAndFormatBroadcastChannels] result missing data.items');
      }

      const formattedItems = result.data.items.map((item: BroadcastChannelItem) => {
        return {
          label: item?.name,
          value: item?.id,
        };
      });

      const finalResult: FetchedFormattedInputChoices = {
        total: result.data.total,
        next: result.data.next,
        prev: result.data.prev,
        items: formattedItems,
      };

      return finalResult;
    } catch (error) {
      console.error('[fetchAndFormatBroadcastChannels] error:', error);
      throw error;
    }
  };

  return { fetchAndFormatBroadcastChannels };
};

/**
 * GET /broadcast/message/ messages
 * Used in Channel search to filter by message_id
 */

export const useGetFetchAndFormatBroadcastMessages = () => {
  const dataProvider = useDataProvider<BloxCustomDataProviderType>();

  const fetchAndFormatBroadcastMessages = async ({
    filterTextDebounced,
    pageParam,
  }: FetchFnParams): Promise<FetchedFormattedInputChoices> => {
    try {
      const payload: GetBroadcastMessageParams = {
        query: {
          dir: 'DESC',
          sort: 'send_date',
          page: pageParam ? Number(pageParam) : undefined,
          limit: 50,
          ...(filterTextDebounced ? { query: filterTextDebounced } : {}),
        },
      };

      const result = await dataProvider.getBroadcastMessage(payload);

      if (!result.data?.items) {
        throw new Error('[fetchAndFormatBroadcastMessages] result missing data.items');
      }

      const formattedItems = result.data.items.map((item: BroadcastMessageItem) => {
        const label = item?.title || item?.message || item?.message_id || 'Untitled';
        return {
          label,
          value: item?.message_id ?? '',
        };
      });

      const finalResult: FetchedFormattedInputChoices = {
        total: result.data.total,
        next: result.data.next,
        prev: result.data.prev,
        items: formattedItems,
      };

      return finalResult;
    } catch (error) {
      console.error('[fetchAndFormatBroadcastMessages] error:', error);
      throw error;
    }
  };

  return { fetchAndFormatBroadcastMessages };
};
